<template>
  <div
    class="font-sans text-dark bg-light antialiased overflow-x-hidden min-h-screen"
  >
    <header
      id="navbar"
      class="fixed w-full top-0 z-50 transition-all duration-300 bg-white shadow-md"
    >
      <div class="container mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex justify-between items-center py-4">
          <button
            @click="goBack"
            class="flex items-center text-[#0A4DA2] font-medium hover:text-secondary transition-colors"
          >
            <i class="fa fa-arrow-left mr-2"></i>
            <span class="text-lg font-bold">{{ langData.btn.back }}</span>
          </button>

          <!-- 右侧：立即咨询 + 语言切换 -->
          <div class="flex items-center space-x-3">
            <button
              @click="goConsult"
              class="bg-[#0A4DA2] hover:bg-[#0A4DA2]/90 text-white px-6 py-1.5 rounded-full transition-all hover:shadow-lg"
            >
              {{ langData.btn.consult }}
            </button>
            <!-- <button
              @click="toggleLang"
              class="bg-[#0A4DA2] hover:bg-[#0A4DA2]/90 text-white px-5 py-1.5 rounded-full transition-all"
            >
              {{ currentLang === "zh" ? "EN" : "简体" }}
            </button> -->
          </div>
        </div>
      </div>
    </header>

    <main class="pt-24 pb-16">
      <div class="container mx-auto px-4 sm:px-6 lg:px-8">
        <div
          class="max-w-7xl mx-auto bg-white rounded-2xl shadow-lg overflow-hidden"
        >
          <div class="w-full">
            <img
              :src="imgUrl"
              alt="服务详情"
              class="w-full h-auto max-h-[80vh] object-contain bg-gray-50"
            />
          </div>

          <div class="p-8 md:p-12">
            <h1
              class="text-[clamp(1.5rem,3vw,2.5rem)] font-bold text-[#0A4DA2] mb-6"
            >
              {{ currentTitle }}
            </h1>
            <p class="text-gray-600 text-lg leading-relaxed mb-4">
              {{ currentDesc }}
            </p>

            <div class="flex flex-wrap gap-3 mt-6">
              <span
                v-for="tag in currentTags"
                :key="tag"
                class="bg-[#0A4DA2]/10 text-[#0A4DA2] px-4 py-1.5 rounded-full text-sm font-medium"
              >
                <i class="fa fa-check-circle mr-1"></i>
                {{ tag }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
const route = useRoute();
const router = useRouter();

const imgUrl = ref("");
const currentLang = ref(localStorage.getItem("siteLang") || "zh");

// 合法 index 校验
const validIndex = [1, 2, 3, 4];
const currentIndex = Number(route.query.index) || 0;

// 非法直接跳首页，不会执行后面代码
if (!validIndex.includes(currentIndex)) {
  router.replace({ path: "/" });
}

// 图片逻辑完全不变
if (currentIndex == 1) {
  imgUrl.value = require("../assets/card1.png");
} else if (currentIndex == 2) {
  imgUrl.value = require("../assets/card2.png");
} else if (currentIndex == 3) {
  imgUrl.value = require("../assets/card3.png");
} else if (currentIndex == 4) {
  imgUrl.value = require("../assets/card4.png");
}

const toggleLang = () => {
  currentLang.value = currentLang.value === "zh" ? "en" : "zh";
  // 存入本地存储
  localStorage.setItem("siteLang", currentLang.value);
};

// 立即咨询跳转
const goConsult = () => {
  window.open(
    "https://work.weixin.qq.com/kfid/kfc964776c9b0909cb6",
    "_blank",
    "noopener noreferrer",
  );
};

const languageMap = {
  zh: {
    btn: { back: "返回", consult: "立即咨询" },
    services: {
      1: {
        title: "保险科技解决方案",
        desc: "构建统一保险业务中台，覆盖投保、核保、出单、理赔、数据监控等全流程能力。",
        tags: ["智能出单", "风控体系", "多渠道对接", "数据监控与报表"],
      },
      2: {
        title: "灵活用工与风控平台",
        desc: "面向物流、配送及平台企业，构建一体化灵活用工运营系统。",
        tags: [
          "用工人员准入",
          "保险自动绑定",
          "风控监测模型及合规",
          "数据运营",
        ],
      },
      3: {
        title: "TPA服务",
        desc: "整合保险机构、医疗服务商、理赔资源与健康服务能力，构建标准化第三方管理平台。",
        tags: [
          "客户与保单管理",
          "理赔流程管理",
          "服务商管理",
          "数据运营与监控",
        ],
      },
      4: {
        title: "行业系统定制开发",
        desc: "基于企业业务场景与管理需求，提供从咨询规划、系统设计、产品开发到运维支持的一站式行业系统交付服务。",
        tags: ["业务架构设计", "运维与升级", "数据治理体系", "安全与权限管理"],
      },
    },
  },
  en: {
    btn: { back: "Back", consult: "Consult Now" },
    services: {
      1: {
        title: "Insurance Technology Solutions",
        desc: "Build a unified insurance business middle office with full-process capabilities covering insurance application, underwriting, policy issuance, claims settlement, data monitoring and more.",
        tags: [
          "Intelligent Policy Issuance",
          "Risk Control System",
          "Multi-channel Connection",
          "Data Monitoring and Reports",
        ],
      },
      2: {
        title: "Flexible Employment & Risk Control Platform",
        desc: "For logistics, distribution and platform enterprises, we build an integrated operational system for flexible employment.",
        tags: [
          "Worker Access Management",
          "Automatic Insurance Binding",
          "Risk Control Monitoring Model & Compliance",
          "Data Operation",
        ],
      },
      3: {
        title: "TPA Service",
        desc: "Integrate insurance institutions, medical service providers, claims resources and health service capabilities to build a standardized third-party management platform.",
        tags: [
          "Customer & Policy Management",
          "Claim Process Management",
          "Service Provider Management",
          "Data Operation and Monitoring",
        ],
      },
      4: {
        title: "Custom Industry System Development",
        desc: "Based on enterprises' business scenarios and management requirements, we provide one-stop industry system delivery services covering consulting planning, system design, product development and operation & maintenance support.",
        tags: [
          "Business Architecture Design",
          "Operation, Maintenance & Upgrade",
          "Data Governance System",
          "Security & Permission Management",
        ],
      },
    },
  },
};

const langData = computed(() => languageMap[currentLang.value]);

// 只加了可选链 ?. 防报错，不影响正常使用
const currentTitle = computed(
  () => langData.value.services[currentIndex]?.title || "",
);
const currentDesc = computed(
  () => langData.value.services[currentIndex]?.desc || "",
);
const currentTags = computed(
  () => langData.value.services[currentIndex]?.tags || [],
);

const goBack = () => {
  router.go(-1);
};

onMounted(() => {
  window.scrollTo(0, 0);
});
</script>

<style scoped>
.animate-fade-in {
  animation: fadeIn 0.8s ease-in-out;
}
@keyframes fadeIn {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}
</style>
